import type { RegisterReadState } from '@repo/front-end-state';
import type { CatalogueReferenceMatch } from '@repo/front-end-services';
import { catalogueReferenceOf } from '@repo/ui';
import type {
  FloorPlannerComposite,
  FloorPlannerItem,
  FloorPlannerCompositeRef,
  FloorPlannerLayout,
} from '@repo/frontend-types';
import { asArray, entityNamesByRefid, type AssetCoverageIndex } from '@repo/front-end-utils';

import { decodeRef, encodeRef } from './compositeRefOptions';
import { compositeVerdictContext, floorNamesOf, planCompositeVerdicts } from './compositeVerdicts';
import {
  computeCompleteness,
  isEmptyMeasure,
  observerCountsFromLayout,
  planMeasures,
  type CompletenessVerdict,
  type MeasureEdge,
} from './entityCoverage';
import { generatedReviewRefids } from './parentSpaceReview';

/**
 * The items behind the Launch summary's counts (`launch-summary-detail`, epic
 * `complex-measures-in-configuration` ledger rows 136–140).
 *
 * The Launch page used to say HOW MANY things were not yet right and leave the operator to find
 * WHICH on four other screens. Each count now opens into a closed-by-default list, and each list is
 * built here, by the same rule as its count, so that the number printed beside a list is that
 * list's length and cannot disagree with it (spec R6).
 */

/** One line in a Launch list. `id` keys the row — display names are not unique on a plan or a register. */
export interface LaunchItem {
  id: string;
  name: string;
  /** Shown after the name, e.g. the catalogue product of an unplaced asset. */
  detail?: string;
}

/** The subset of a register row a Launch list reads. */
export interface LaunchRegisterRow {
  id?: number | null;
  /** Customer's raw asset name — the only column the CSV requires. */
  name?: string | null;
  /**
   * TwinLabs' unique-ified display name. Not declared on `TwinAssetResponse`, but present on the
   * rows at runtime — `AssetRegisterList` reads it from the very same `registerRows` the page hands
   * it — so it is read here the same way, falling back to `name`.
   */
  asset_name?: string | null;
  /** Matched catalogue product; null when there is none. */
  product?: string | null;
}

/**
 * A register row as a Launch line: the register table's display name (`asset_name`, else `name`),
 * so the operator reads the same words on both screens.
 */
export const registerRowItem = (
  row: LaunchRegisterRow,
  position: number,
  { withProduct = false }: { withProduct?: boolean } = {},
): LaunchItem => {
  const name = row.asset_name?.trim() || row.name?.trim() || `Asset ${row.id ?? ''}`.trim();
  const product = withProduct ? row.product?.trim() : undefined;
  /*
   * ⚠ The id keys a React list, so it must be unique within the list. A register row's id always
   * is; a row WITHOUT one falls back to its position in the list, never to its name — display
   * names are not unique, and two id-less rows called "Bench" would otherwise share a key. The
   * `row-` prefix keeps a position from colliding with a real numeric id.
   */
  const id = row.id != null ? String(row.id) : `row-${position}`;
  return { id, name, ...(product ? { detail: product } : {}) };
};

/** A plan entity as a Launch line — its refid is the only unique thing about it. */
export const entityItem = (entity: { refid: string; name: string }): LaunchItem => ({
  id: entity.refid,
  name: entity.name,
});

/**
 * The register rows the plan does not hold — the Floor Plan block's *not yet placed* list.
 *
 * ⚠ **The register table's rule, not arithmetic.** This used to be
 * `registerRows.length - placedItems.length`, a subtraction of two set sizes that is wrong whenever
 * something is placed from the Library (no register row behind it) or a register row is placed
 * twice. It is now the set the register table's *Not on plan* coverage column marks —
 * `statusFor(id) === 'not-on-plan'` over the same coverage index — so the two screens agree.
 *
 * ⚠ **`known: false` when the plan has not been read.** Every row answers `not-on-plan` against a
 * null layout, so without this the block would state "700 not yet placed" about a twin whose plan
 * is still loading, or failed to load — an affirmative claim on the strength of a read that did not
 * happen. `planKnown` is the index's own answer to that question.
 */
export const unplacedRegisterItems = (
  rows: readonly LaunchRegisterRow[],
  index: Pick<AssetCoverageIndex, 'planKnown' | 'statusFor'>,
): { known: false } | { known: true; items: LaunchItem[] } => {
  if (!index.planKnown) return { known: false };
  return {
    known: true,
    items: rows
      /*
       * `Number(row.id)`, not a type guard: a row whose id is not a positive integer answers
       * `not-on-plan` from `statusFor`, and the register table counts it so — skipping it here
       * would make this list one short of that table on exactly the malformed row.
       */
      .filter(row => index.statusFor(Number(row.id)) === 'not-on-plan')
      .map((row, position) => registerRowItem(row, position, { withProduct: true })),
  };
};

/** The plan read's own state, as `usePlanLoad` tracks it. */
export type LaunchPlanReadState = 'loading' | 'none' | 'loaded' | 'failed' | 'unread';

/**
 * What the Launch Floor Plan block can say about placement, and why when it cannot.
 *
 * ⚠ **Two reads, and a failure names the one that failed.** The unplaced list needs BOTH the plan
 * and the register. A register that is still loading, or failed, is not a floor plan that could not
 * be read — so the block keeps "Plan drawn — N placed", which the plan alone answers, and says
 * only that the unplaced part waits on the register (spec, Failure States: the plan-read row's
 * Partial cell).
 */
export type LaunchPlacement =
  | { kind: 'plan-loading' }
  | { kind: 'plan-error' }
  | { kind: 'register-unread'; register: 'loading' | 'error' | 'no-twin' }
  | { kind: 'ready'; unplaced: LaunchItem[] };

/**
 * The Floor Plan block's placement state (`launch-summary-detail`, R3), lifted out of
 * `OnboardingPage` so every branch is reachable from a test.
 *
 * - A plan read that FAILED, or one that is not coming (`unread`: no twin BID), is the plan's own
 *   failure and wins over everything: a layout left in the store is not this twin's answer. It is
 *   an error rather than a loading state, which would spin for ever.
 * - A plan read in flight is loading.
 * - The plan read has SETTLED (`none` or `loaded`) but the coverage index cannot say what is
 *   placed — the session holds no layout. That is a session with no plan, not an unread one: a twin
 *   with no saved plan, or one whose store was emptied after its plan was read (the read state
 *   stays where it was). Nothing is placed, so every register row is unplaced.
 * - Otherwise the register decides: not ready ⇒ say so and list nothing; ready ⇒ the list.
 */
export const launchPlacementState = ({
  planState,
  registerRead,
  rows,
  index,
}: {
  planState: LaunchPlanReadState;
  registerRead: RegisterReadState;
  rows: readonly LaunchRegisterRow[];
  index: Pick<AssetCoverageIndex, 'planKnown' | 'statusFor'>;
}): LaunchPlacement => {
  if (planState === 'failed' || planState === 'unread') return { kind: 'plan-error' };
  if (planState === 'loading') return { kind: 'plan-loading' };
  if (registerRead !== 'ready') return { kind: 'register-unread', register: registerRead };
  const known = unplacedRegisterItems(rows, index);
  if (known.known) return { kind: 'ready', unplaced: known.items };
  const nothingPlaced = unplacedRegisterItems(rows, {
    planKnown: true,
    statusFor: () => 'not-on-plan',
  });
  return { kind: 'ready', unplaced: nothingPlaced.known ? nothingPlaced.items : [] };
};

/**
 * The Launch Observers block's read state (`launch-summary-detail`, R4).
 *
 * ⚠ **The PLAN half too, since the block names entities.** The twin's configuration read settles
 * before its plan lands and before the live observers are matched onto that plan, so on the
 * configuration signal alone the block listed every entity as observer-less — ones the live twin
 * does observe included — beside a coverage block honestly still reading. `plan` is the coverage
 * block's own answer to "is the plan in hand WITH the twin's configuration on it", and only its
 * `loading` holds this block back. `absent` stays ready: a twin with no plan has no entities, 0 and
 * 0 is the true answer, and holding it would park the block on "still reading" for ever.
 */
export const observersReadState = (
  configuration: 'pending' | 'ready' | 'failed',
  plan: 'loading' | 'absent' | 'present',
  planRead: LaunchPlanReadState,
): 'loading' | 'ready' | 'error' | 'plan-error' =>
  configuration === 'failed'
    ? 'error'
    : configuration === 'pending' || plan === 'loading'
      ? 'loading'
      : /*
         * ⚠ `absent` is only "0 and 0" when the plan read ANSWERED that there is no plan. A plan read
         * that failed, or one that is not coming (no twin BID), also leaves no plan in the session,
         * and "0 without one yet" there is an affirmative claim that nothing lacks an observer, on
         * the strength of a read that did not happen. It names the plan, not the observers, as what
         * could not be read — the same rule the Floor Plan block follows. A plan drawn in this
         * session is `present`, and its own entities are the true answer whatever the read did.
         * (Copilot review, PR #1819.)
         */
        plan === 'absent' && (planRead === 'failed' || planRead === 'unread')
        ? 'plan-error'
        : 'ready';

/** Names a composite reference as the operator reads it — see {@link nameMeasureTarget}. */
const nameRef = (
  ref: FloorPlannerCompositeRef | null | undefined,
  names: ReadonlyMap<string, string>,
  twinName: string,
  floorNames: ReadonlyMap<number, string>,
): string => {
  /*
   * ⚠ The plan's CURRENT name, falling back to the identifier — never to a generic word. A line
   * reading "Zone is not finished" sends whoever reads it looking for a zone called Zone.
   */
  if (ref && ref.kind === 'entity') return names.get(ref.refid) ?? ref.refid;
  /*
   * ⚠ A floor by its plan NAME, as the Composite measures step names it — "Ground Floor", never
   * "Floor 1" beside a sentence saying "Ground Floor" (`composites-page-launch-refusals`, caught by
   * the before/after capture). `Floor {id}` only where the plan gives it no name.
   */
  if (ref && ref.kind === 'floor') return floorNames.get(ref.floorId) ?? `Floor ${ref.floorId}`;
  /*
   * ⚠ The twin's NAME first, and the composites stage's own list is why. That list calls the
   * twin-level total "Station - TwinSense"; a Launch line calling the same measure "NP2-STN" makes
   * an operator look for two different things. Caught by the before/after capture, not by a test.
   */
  return twinName;
};

/**
 * What a composite measure is the total OF, as the operator reads it — a total has no name of its
 * own, so it is named for its target (`launch-composite-refusals`, R6).
 *
 * One helper for every Launch line that names a measure — the unfinished, duplicate and empty
 * groups — and for Go Live's refusal, so the four cannot call one measure four different things.
 */
export const nameMeasureTarget = (
  target: string,
  names: ReadonlyMap<string, string>,
  /** The twin's display name, for a twin-level total — resolved by the caller. */
  twinName: string,
  /** Each floor's plan name by id — see {@link floorNamesOf}. Absent, a floor reads `Floor {id}`. */
  floorNames: ReadonlyMap<number, string> = new Map(),
): string => nameRef(decodeRef(target), names, twinName, floorNames);

/** Each floor's plan name by id — moved to `compositeVerdicts.ts`, re-exported for Launch's callers. */
export { floorNamesOf };

/** The stand-in refid prefix for a composite row the plan stored with none — see `refusedComposites`. */
const UNIDENTIFIED_REFID = 'unidentified-composite:';

/**
 * The plan's composites with a stand-in refid for every object row whose stored one cannot identify
 * it — absent, not a string, blank, or REPEATED — the ONE place that identity is given, so the
 * verdicts and the Launch groups see the same rows under the same keys.
 *
 * The graph rules use the refid as a row's self-identity (`findDuplicateTarget` skips a row with
 * the candidate's refid; the edges substitute the candidate for whatever holds that refid), so two
 * live rows sharing one hide each other — a duplicated target goes unseen — while the statement
 * builder, which never reads a refid, files both. The first row to hold a refid keeps it; every
 * later holder gets a stand-in. (Copilot review of PR #1897, rounds 2 and 3.)
 */
const withStandInRefids = (
  composites: FloorPlannerLayout['composites'] | null | undefined,
): FloorPlannerComposite[] => {
  const seen = new Set<string>();
  return asArray(composites).map((composite, index) => {
    if (!composite || typeof composite !== 'object') return composite;
    const refid = composite.refid;
    if (typeof refid === 'string' && refid.trim() !== '' && !seen.has(refid)) {
      seen.add(refid);
      return composite;
    }
    return { ...composite, refid: `${UNIDENTIFIED_REFID}${index}` };
  });
};

/** One name per refused TARGET, in plan order — folded by encoded target, never by display name. */
export const refusedTargetNames = (
  refused: readonly { refid: string; target: string; name: string }[],
): string[] => {
  const byTarget = new Map<string, string>();
  for (const r of refused) {
    const key = r.target === '' ? `${UNIDENTIFIED_REFID}${r.refid}` : r.target;
    if (!byTarget.has(key)) byTarget.set(key, r.name);
  }
  return Array.from(byTarget.values());
};

/** A composite measure the twin would refuse, as a Launch line names it. */
export interface RefusedComposite {
  refid: string;
  /** Its encoded target (`encodeRef`) — what someone looking it up in the plan matches on. */
  target: string;
  /** What it is the total OF, as the operator reads it — see {@link nameMeasureTarget}. */
  name: string;
  /** The step's own sentences for it, verbatim and in the step's order. */
  reasons: string[];
}

/** The plan's composite measures, sorted into the non-refused Launch groups — refids, not yet named. */
interface LaunchCompositeClasses {
  /** Measures that add up nothing. Filed as drafts; they never block. */
  empty: string[];
  /** Measures short of something they add up. Never block either. */
  /** Not finished, with something measured: goes live short. */
  unfinished: string[];
  /** Not finished and nothing measured: held back from the live system. */
  nothingMeasured: string[];
  /**
   * Generated totals a person should look at — equipment in the space that the total leaves out, or
   * a floor the twin's total cannot include (`complex-measures-in-configuration-parent-space-
   * composites`, FR11a). LAST in precedence, so a measure already listed above is not listed twice
   * (Rob's "one measure, one line"), and it never blocks.
   */
  review: string[];
}

/**
 * Sort the plan's measures that are NOT refused into the remaining Launch groups, each in AT MOST
 * one (`composites-page-launch-refusals`, R4). A finished measure that nothing refuses is in none —
 * it is not a problem to report.
 *
 * ⛔ **Precedence is refused, then empty, then nothing measured, then unfinished (partly measured),
 * then review — and the order is the rule.** A refused total is the only kind that stops Go Live, so it must never be hidden inside a
 * group that says "this does not block you": an empty total on a target two cameras watch is refused
 * (`target-collision`) and is listed as refused, not as empty. And Rob's ruling (2026-09-30) is that
 * an empty measure is listed as empty, not ALSO as unfinished — one measure, one line, so no two
 * counts on the screen overlap.
 *
 * `refusedRefids` comes from the shared verdicts ({@link planCompositeVerdicts}); the refused group
 * itself is built from the live composites by {@link launchCompositeGroups}, because `measures`
 * (`planMeasures`) drops a total whose target cannot be encoded — and that total is refused too.
 */
export const classifyLaunchComposites = ({
  measures,
  verdicts,
  refusedRefids = new Set<string>(),
  reviewRefids = new Set<string>(),
}: {
  measures: readonly MeasureEdge[];
  verdicts: ReadonlyMap<string, CompletenessVerdict>;
  /** Refids of every measure the twin would refuse. */
  refusedRefids?: ReadonlySet<string>;
  /** Refids of the generated totals `generatedReviews` flags. */
  reviewRefids?: ReadonlySet<string>;
}): LaunchCompositeClasses => {
  const empty = measures
    .filter(m => !refusedRefids.has(m.refid) && isEmptyMeasure(m))
    .map(m => m.refid);
  // An excluded total is published nowhere, so neither "goes live" nor "held back until it has a
  // camera" is true of it; it is in neither line (Rob, 2026-10-07). Its row says *Not published*.
  const notFinished = measures.filter(
    m =>
      !refusedRefids.has(m.refid) &&
      !isEmptyMeasure(m) &&
      m.excluded !== true &&
      verdicts.get(m.refid)?.fullyConfigured === false,
  );
  // Split by what reaches the live system (`onboarding-polish-partial-composites`): a total with
  // a measured source goes live short; one with nothing measured is held back. Two sentences,
  // because one would be false of half of them.
  const nothingMeasured = notFinished
    .filter(m => verdicts.get(m.refid)?.measured !== true)
    .map(m => m.refid);
  const unfinished = notFinished
    .filter(m => verdicts.get(m.refid)?.measured === true)
    .map(m => m.refid);
  const listed = new Set(Array.from(refusedRefids).concat(empty, nothingMeasured, unfinished));
  const review = measures
    .filter(m => !listed.has(m.refid) && reviewRefids.has(m.refid))
    .map(m => m.refid);
  return { empty, unfinished, nothingMeasured, review };
};

/** The Launch groups with their names — what `coverageBlock` takes. */
interface LaunchCompositeGroups {
  refused: RefusedComposite[];
  /**
   * `'failed'` when the refusal check itself threw — a defect in the verdict code, since it does no
   * I/O. Launch then says it could not check, and does not hold (spec R6a).
   */
  refusalCheck: 'ok' | 'failed';
  empty: { refid: string; name: string }[];
  unfinished: { refid: string; name: string }[];
  nothingMeasured: { refid: string; name: string }[];
  review: { refid: string; name: string }[];
}

/** What every call below needs to name a line and to judge the plan the way the step does. */
interface LaunchCompositeContext {
  layout: FloorPlannerLayout | null | undefined;
  /** The twin's display name, for naming a twin-level line. Resolved ONCE by the page for both callers. */
  twinName: string;
  /** The twin's BID — what filing prefixes a floor's and an entity's identity with. */
  twinBid: string | null | undefined;
  /**
   * The twin's name EXACTLY as the Composite measures step passes it to `compositeRefOptions` (the
   * twin record's name, possibly absent), so a sentence naming the whole twin reads the same on
   * Launch as on the step. Not `twinName`, whose fallbacks differ.
   */
  refTwinName: string | undefined;
  /** The verdict pass. A parameter only so a test can make it throw; production never passes it. */
  verdictsOf?: typeof planCompositeVerdicts;
}

/**
 * Every live measure the twin would refuse, in plan order, named — from the SAME verdicts the
 * Composite measures step shows (`composites-page-launch-refusals`, R4). `names` and `floors` are
 * the plan's entity and floor names, built once by the caller and shared with its other lines.
 *
 * Throws whatever the verdict pass throws; both callers catch it.
 */
const refusedComposites = (
  {
    layout,
    twinName,
    twinBid,
    refTwinName,
    verdictsOf = planCompositeVerdicts,
  }: LaunchCompositeContext,
  names: ReadonlyMap<string, string>,
  floors: ReadonlyMap<number, string>,
): RefusedComposite[] => {
  /*
   * ⛔ **A row with no string refid is JUDGED, under a stand-in identity — never left out.** The plan
   * layout is a JSON column that guarantees nothing about its shape, and the statement builder files
   * a row's target and inputs without ever reading its refid, so such a row goes live like any other.
   * Dropping it here would let a refusal only the wizard can see (a contained double count, a pair
   * the plan cannot decide) through both this hold and Go live's guard. The verdict pass keys every
   * answer by refid, so each such row gets a unique stand-in first — two of them sharing `undefined`
   * would pool their sentences — and keeps it as its list key. (Copilot review of PR #1897.)
   */
  const composites = withStandInRefids(layout?.composites);
  const context = compositeVerdictContext({
    composites,
    layout,
    twinBid,
    twinName: refTwinName,
  });
  const { problems } = verdictsOf(context);
  const unidentified = context.live.filter(
    composite =>
      typeof composite.refid === 'string' && composite.refid.indexOf(UNIDENTIFIED_REFID) === 0,
  );
  if (unidentified.length > 0) {
    // eslint-disable-next-line no-console
    console.warn('[launch] composite rows with no refid were judged under a stand-in identity', {
      twinBid,
      count: unidentified.length,
      targets: unidentified.map(composite => composite.target),
    });
  }
  return (
    context.live
      // A non-object entry ("junk" in the JSON) has no target to file: the builder refuses it outright.
      .filter(composite => typeof composite.refid === 'string')
      .flatMap(composite => {
        const reasons = problems.get(composite.refid) ?? [];
        if (reasons.length === 0) return [];
        const target = encodeRef(composite.target);
        return [
          {
            refid: composite.refid,
            target,
            /*
             * A target that cannot be encoded names nothing on the plan — `nameMeasureTarget` would
             * fall through to the TWIN's name for it, which is a different total. Named for what it is.
             */
            name:
              target === ''
                ? 'A composite measure with an unreadable target'
                : nameMeasureTarget(target, names, twinName, floors),
            reasons,
          },
        ];
      })
  );
};

/**
 * Classify the plan's measures and name every line, from the layout alone.
 *
 * The layout is the one Go Live files, so a refusal found here is a refusal of the plan Go Live
 * would file right now, whatever the twin's configuration read is doing — which is why this takes no
 * read state.
 */
export const launchCompositeGroups = (context: LaunchCompositeContext): LaunchCompositeGroups => {
  const { layout, twinName, twinBid } = context;
  // The same stand-in identities `refusedComposites` judges under, so a refused row is excluded
  // from every later group rather than reappearing there under no key.
  const measures = planMeasures(withStandInRefids(layout?.composites));
  const verdicts = computeCompleteness({
    observerCountsByRef: observerCountsFromLayout(layout),
    measures,
  });

  const names = entityNamesByRefid(layout ?? null);
  const floors = floorNamesOf(layout);
  let refused: RefusedComposite[] = [];
  let refusalCheck: 'ok' | 'failed' = 'ok';
  try {
    refused = refusedComposites(context, names, floors);
  } catch (error) {
    /*
     * ⚠ **Not held, and saying so.** The check does no I/O, so a throw is a defect in it — and
     * holding Go live on a broken check would block every launch for a bug in this code, while the
     * server and the statement builder still refuse a bad total on their own. "Nothing is refused"
     * would be the false claim; "could not check" is the true one (spec R6a, Failure States).
     */
    // eslint-disable-next-line no-console
    console.error('[launch] composite refusal check failed', { twinBid, error });
    refusalCheck = 'failed';
  }

  const reviewRefids = generatedReviewRefids(layout);
  const classes = classifyLaunchComposites({
    measures,
    verdicts,
    refusedRefids: new Set(refused.map(r => r.refid)),
    reviewRefids,
  });
  // Every refid below came from `measures`, so each has a target to name.
  const targetOf = new Map(measures.map(m => [m.refid, m.target]));
  const named = (refid: string) => ({
    refid,
    name: nameMeasureTarget(targetOf.get(refid) as string, names, twinName, floors),
  });
  return {
    refused,
    refusalCheck,
    empty: classes.empty.map(named),
    unfinished: classes.unfinished.map(named),
    nothingMeasured: classes.nothingMeasured.map(named),
    review: classes.review.map(named),
  };
};

/**
 * Go Live's own refusal of a total the twin would refuse — the sentence to show and what to log
 * (`composites-page-launch-refusals`, R7).
 *
 * ⚠ **A second guard, and not reachable through today's UI.** The Launch button is disabled by the
 * same layout this reads, so a store change re-renders it disabled before it can be pressed. It is
 * here for any caller of the action that does not go through that button, and it stops BEFORE
 * anything is written. For most refusals filing or the statement builder would refuse the whole
 * statement anyway, only later; for the two CONTAINMENT refusals (a contained double count, a pair
 * the plan cannot decide) nothing downstream can see plan geometry, so this and Launch's hold are
 * the only stop — never weaken either as redundant.
 *
 * - `null` — nothing is refused; carry on.
 * - `{ message, refused }` — stop, show `message`, log `refused`.
 * - `{ checkFailed, error }` — the check itself threw. Carry on: the server and the builder are still
 *   the authority, and a broken check must not block every launch (spec R6a).
 */
export const goLiveCompositeRefusal = ({
  actionLabel,
  ...context
}: LaunchCompositeContext & {
  /** The button's own label — `Go live`, or `Update twin` on an established twin. */
  actionLabel: string;
}):
  | {
      message: string;
      refused: { refid: string; target: string; name: string; reasons: string[] }[];
    }
  | { checkFailed: true; error: unknown }
  | null => {
  let refused: RefusedComposite[];
  try {
    refused = refusedComposites(
      context,
      entityNamesByRefid(context.layout ?? null),
      floorNamesOf(context.layout),
    );
  } catch (error) {
    return { checkFailed: true, error };
  }
  if (refused.length === 0) return null;
  /*
   * One name per TARGET: two totals on one target are two refused measures and ONE thing to fix,
   * and "Whole gym, Whole gym" reads as a defect in the message rather than in the plan. Folded by
   * target, never by display name — two different spaces called "Studio" are two things to fix.
   */
  const targets = refusedTargetNames(refused);
  return {
    message:
      `${actionLabel} was stopped before anything was changed: ${targets.join(', ')} ` +
      `would be refused. Fix ${targets.length === 1 ? 'it' : 'them'} in the Composite measures step.`,
    refused: refused.map(({ refid, target, name, reasons }) => ({ refid, target, name, reasons })),
  };
};

/* -------------------------------------------------------------------------------------------- *
 * BI hierarchies (`launch-bi-gap-preview`, R2)
 * -------------------------------------------------------------------------------------------- */

/** The detail an item with no catalogue reference carries in the BI list. */
export const NO_CATALOGUE_MATCH_DETAIL = 'No catalogue match in the asset register';

/**
 * The headings BI's two kinds of miss are listed under, on Launch and in the post-launch summary
 * alike (R2, R3.4), so the two lists cannot name one kind differently.
 */
export const BI_GAP_HEADINGS = {
  notInManufacturer: 'Not in BI’s manufacturer catalogue',
  noEquipmentType: 'No equipment type in BI',
} as const;

/** Whether the section's inputs — the plan and the register — can be read yet (R2.3). */
export type BiInputs = 'reading' | 'ready' | 'register-error' | 'plan-error';

/**
 * The section's inputs, from the Floor Plan block's placement state: the plan's own failure wins; a
 * register that failed (or has no twin to read) is an error; anything still loading is reading. A
 * register still loading is NOT an error — it would say "could not be read" while it is being read.
 */
export const biInputsOf = (placement: LaunchPlacement): BiInputs => {
  if (placement.kind === 'ready') return 'ready';
  if (placement.kind === 'plan-error') return 'plan-error';
  if (placement.kind === 'register-unread' && placement.register !== 'loading') {
    return 'register-error';
  }
  return 'reading';
};

/** BI's answer as the Launch section reads it: absent while unknown, `failed` when it could not be had. */
export type BiCheckAnswer =
  | { status: 'ready'; references: readonly CatalogueReferenceMatch[] }
  | { status: 'failed' }
  | undefined;

/**
 * The placed items that will reach BI with a gap, in three disjoint groups, each a list whose length
 * is the count printed beside it.
 */
export interface BiGapGroups {
  /** No register link, or a register row with no catalogue reference. Known before BI answers. */
  noReference: LaunchItem[];
  /** BI's answer: `NO_MATCH_MANUFACTURER`. Empty until BI answers. */
  notInManufacturer: LaunchItem[];
  /** BI's answer: `NO_MATCH_EQUIP_TYPE`. Empty until BI answers. */
  noEquipmentType: LaunchItem[];
  /** The distinct references to ask BI about, sorted: the check is keyed on this set. */
  references: string[];
}

type BiGapItem = Pick<FloorPlannerItem, 'refid' | 'name' | 'entityCode' | 'assetUid'>;

/**
 * Every placed item, grouped by what BI will be missing for it (R2.1–R2.2). Each item's reference
 * is `catalogueReferenceOf` — the lookup Go Live writes into `definitions.equipment_id` — so the
 * preview asks BI about exactly what the launch will send. An item with a reference BI has not
 * answered for (no answer yet, or a failed one) is in no group: it is unknown, not a gap.
 */
export const biGapGroups = (
  placedItems: readonly BiGapItem[],
  referenceIdByAssetUid: ReadonlyMap<string, string>,
  answer: BiCheckAnswer,
): BiGapGroups => {
  const issueByReference = new Map<string, CatalogueReferenceMatch['issue']>();
  if (answer?.status === 'ready') {
    answer.references.forEach(entry => issueByReference.set(entry.reference_id, entry.issue));
  }

  const groups: BiGapGroups = {
    noReference: [],
    notInManufacturer: [],
    noEquipmentType: [],
    references: [],
  };
  const references = new Set<string>();
  placedItems.forEach(item => {
    // The plan item's name, else its entity code — as `entityItem` names entities on this page.
    const name = item.name?.trim() || item.entityCode?.trim() || item.refid;
    const reference = catalogueReferenceOf(item, referenceIdByAssetUid);
    if (reference === undefined) {
      groups.noReference.push({ id: item.refid, name, detail: NO_CATALOGUE_MATCH_DETAIL });
      return;
    }
    references.add(reference);
    const issue = issueByReference.get(reference);
    if (issue === 'NO_MATCH_MANUFACTURER') {
      groups.notInManufacturer.push({ id: item.refid, name, detail: reference });
    } else if (issue === 'NO_MATCH_EQUIP_TYPE') {
      groups.noEquipmentType.push({ id: item.refid, name, detail: reference });
    }
  });
  groups.references = Array.from(references).sort();
  return groups;
};
